import {
  baixadoBacklogInicial,
  perdidoBacklogInicial
} from "../../domain/backlog.ts";
import type { Id } from "../../domain/ids.ts";
import type { Store } from "../store.ts";
import { clear, el, novoEstadoTabela, openModal, tabelaPaginada, type Coluna, botao } from "../ui.ts";
import { multiSelect } from "./_multi-select.ts";
import { toast } from "../toast.ts";

export function abrirBacklogInicial(
  store: Store,
  aoFechar?: () => void,
  clienteIdPadrao?: Id,
): void {
  openModal("Backlog Inicial", (fechar) => {
    let snap = store.get();

    // Preparar dados e filtros
    const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));
    const clientesAtivos = [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    const catsUsadas = [...snap.categorias]
      .filter((c) => snap.tiposPeca.some((t) => t.ativo && t.categoriaId === c.id))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const msCategoria = multiSelect({
      rotulo: "Categoria",
      itens: catsUsadas.map((c) => ({ id: c.id, nome: c.nome })),
      todosPorPadrao: true,
      aoMudar: () => desenhar(),
    });

    const msHoteis = multiSelect({
      rotulo: "Hotéis",
      itens: clientesAtivos.map((h) => ({ id: h.id, nome: h.nome })),
      ...(clienteIdPadrao && clientesAtivos.some((c) => c.id === clienteIdPadrao)
        ? { marcadosPorPadrao: [clienteIdPadrao] }
        : {}),
      todosPorPadrao: !clienteIdPadrao,
      aoMudar: () => desenhar(),
    });

    const est = novoEstadoTabela(1); // Ordena por Categoria ▲ originalmente, ou Tipo
    est.size = 50;

    const alvo = el("div");
    const erro = el("p", { class: "form-erro", role: "alert" });

    // Estado da edição: para não perder edições ao filtrar
    const rascunho = new Map<string, number>();
    const rascunhoDirty = new Set<string>(); // IDs de hotéis que sofreram alguma edição

    // Popula o estado inicial da tabela no rascunho. Isto evita dor de cabeça com missing values
    for (const b of snap.backlogInicial) {
      rascunho.set(`${b.clienteId}|${b.tipoId}`, b.quantidade);
    }

    const desenhar = (): void => {
      clear(erro);
      const hotelIds = new Set(msHoteis.selecionados());
      const hoteis = clientesAtivos.filter((h) => hotelIds.has(h.id));

      const catIds = new Set(msCategoria.selecionados());
      const tipos = [...snap.tiposPeca]
        .filter((t) => t.ativo)
        .filter((t) => catIds.size === 0 || catIds.has(t.categoriaId))
        .sort((a, b) => {
          const c = (catNome.get(a.categoriaId) ?? "").localeCompare(
            catNome.get(b.categoriaId) ?? "",
            "pt-BR",
          );
          return c !== 0 ? c : a.nome.localeCompare(b.nome, "pt-BR");
        });

      const colunas: Coluna[] = [
        { t: "Tipo de Peça", w: 220 },
        { t: "Categoria", w: 130 },
        ...hoteis.map((h): Coluna => ({ t: h.nome, w: 140 })),
      ];

      const linhas = tipos.map((t) => {
        return el(
          "tr",
          {},
          el("td", {}, t.nome),
          el("td", { class: "muted" }, catNome.get(t.categoriaId) ?? "—"),
          ...hoteis.map((h) => {
            const key = `${h.id}|${t.id}`;
            const consumido = baixadoBacklogInicial(snap, h.id, t.id) + perdidoBacklogInicial(snap, h.id, t.id);
            const val = rascunho.get(key) ?? 0;

            const input = el("input", {
              type: "number",
              min: String(consumido),
              step: "1",
              placeholder: "0",
              title: consumido > 0 ? `Já saíram ${consumido} peça(s). Mínimo permitido é ${consumido}.` : "",
              "aria-label": `Backlog inicial de ${t.nome} para ${h.nome}`,
            }) as HTMLInputElement;

            input.value = val === 0 ? "" : String(val);

            input.addEventListener("input", () => {
              erro.textContent = "";
              const newValue = Number(input.value) || 0;
              rascunho.set(key, newValue);
              rascunhoDirty.add(h.id);
            });

            return el("td", { class: "num" }, input);
          })
        );
      });

      clear(alvo);
      if (tipos.length === 0 || hoteis.length === 0) {
          alvo.append(el("p", { class: "muted mini-vazio" }, "Sem dados para exibir com os filtros atuais."));
      } else {
          alvo.append(
            tabelaPaginada("matriz-backlog", colunas, linhas, est, desenhar),
          );
      }
    };
    desenhar();

    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);

    // Ação primária do sistema: fora de um <form>, a Matriz de Preços usa
    // `btn-primario` (tabela-precos.ts) — mesmo padrão aplicado aqui.
    const salvar = botao("primario", {}, "Salvar");

    salvar.addEventListener("click", async () => {
      erro.textContent = "";
      if (rascunhoDirty.size === 0) {
        fechar();
        aoFechar?.();
        return;
      }

      salvar.disabled = true;
      let sucessoTratado = 0;

      try {
        for (const clienteId of rascunhoDirty) {
          // Prepara a lista de todas as linhas de backlog para aquele cliente
          const linhasCmd = snap.tiposPeca
            .map(t => {
              const val = rascunho.get(`${clienteId}|${t.id}`) ?? 0;
              return { tipoId: t.id, quantidade: val };
            })
            // zero = sem registro; negativo vai pro domínio, que recusa com a
            // mensagem (antes era descartado aqui sem aviso)
            .filter(l => l.quantidade !== 0);

          await store.executar(
            { agregado: "backlogInicial", cmd: { tipo: "definir", clienteId, linhas: linhasCmd } },
            null, // sem toast automático do Store, pra não encher a tela caso sejam mtos hoteis
            (msg) => { throw new Error(`Erro em ${clientesAtivos.find(c => c.id === clienteId)?.nome}: ${msg}`); }
          );
          sucessoTratado++;
        }

        toast(`Backlog inicial atualizado para ${sucessoTratado} hotel(is).`);
        fechar();
        aoFechar?.();
      } catch (err: any) {
        erro.textContent = err.message || String(err);
        salvar.disabled = false;
      }
    });

    return el(
      "div",
      { class: "matriz-precos backlog-inicial" }, // Reaproveita as classes base da Matriz de Preços
      el(
        "div",
        { class: "matriz-barra" },
        msCategoria.element,
        msHoteis.element,
      ),
      el(
        "p",
        { class: "muted" },
        "Saldo que já existia nos clientes antes do sistema entrar em operação. " +
        "Preencha todas as quantidades e clique em Salvar ao final.",
      ),
      alvo,
      erro,
      el("div", { class: "modal-actions" }, cancelar, salvar),
    );
  }, "amplo");
}
